import { useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProjectTable } from "@/components/projects/ProjectTable";
import {
  SORT_KEYS,
  nextSortState,
  sortActiveProjects,
  sortCompletedRows,
  toCompletedRow,
  type SortKey,
  type SortDir,
} from "@/components/projects/project-sort";
import { CompletedProjectsTable } from "@/components/projects/CompletedProjectsTable";
import { useProjectStorage } from "@/hooks/use-project-storage";
import { useProjects } from "@/hooks/use-projects";
import { useUsers, buildUserMap } from "@/hooks/use-users";
import { useProjectOwners, buildOwnersMap } from "@/hooks/use-project-owners";
import { useProjectHours } from "@/hooks/use-project-hours";
import { useProjectInvoiced } from "@/hooks/use-project-invoiced";
import { useProjectProgress } from "@/hooks/use-project-progress";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useProjectListKeyboard } from "@/hooks/use-project-list-keyboard";
import { cn } from "@/lib/utils";
import { SrIcon, type SrIconName } from "@/components/ui/SrIcon";
import { matchesCompletedPeriod, MONTH_NAMES_NB } from "@/lib/completed-period-filter";

type StatusFilter = "active" | "kommende" | "completed" | "all";
type RoleFilter = "pl" | "sales" | "advisor";
const ROLE_FILTERS: RoleFilter[] = ["pl", "sales", "advisor"];
const ROLE_FILTER_SET = new Set<RoleFilter>(ROLE_FILTERS);
const STATUS_FILTERS = new Set<StatusFilter>(["active", "kommende", "completed", "all"]);

// Lagrer siste filter-state så sidebar-/command-palette-navigering til
// /prosjekter (uten query) gjenoppretter det Erik hadde sist. URL-stringen
// er fortsatt SOT under navigering — storage brukes bare som fallback når
// URL-en er naken.
const FILTERS_STORAGE_KEY = "projects-page-filters-v1";
// VIB-954: filteret skal huskes ved refresh, men ikke "for lenge". Etter denne
// TTL-en regnes lagret state som foreldet og vi faller tilbake til default-visning
// (Aktive). Eldre lagring uten tidsstempel regnes også som foreldet.
// Bumpet til 2t så filter OG sortering (William 29.06) overlever en arbeidsøkt
// med navigering inn/ut av prosjekter.
const FILTERS_TTL_MS = 2 * 60 * 60 * 1000; // 2 t

interface StoredFilters {
  status?: StatusFilter;
  q?: string;
  roles?: string;
  sort?: string;
  dir?: string;
  savedAt?: number;
}

function readStoredFilters(): StoredFilters | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredFilters;
    // Foreldet (eller pre-TTL uten tidsstempel) → ignorer og rydd opp.
    if (!parsed.savedAt || Date.now() - parsed.savedAt > FILTERS_TTL_MS) {
      localStorage.removeItem(FILTERS_STORAGE_KEY);
      return null;
    }
    if (parsed.status && !STATUS_FILTERS.has(parsed.status)) parsed.status = undefined;
    return parsed;
  } catch {
    return null;
  }
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: projects, isLoading, error } = useProjects();
  const { data: users } = useUsers();
  const { data: owners } = useProjectOwners();
  const { data: hoursMap } = useProjectHours();
  const { data: invoicedMap } = useProjectInvoiced();
  const { data: progressMap } = useProjectProgress();
  const { activeUser } = useImpersonation();
  // URL-persisted filters
  const search = searchParams.get("q") ?? "";
  const statusFilter = (searchParams.get("status") ?? "active") as StatusFilter;
  const roleFilters = useMemo<Set<RoleFilter>>(() => {
    const raw = searchParams.get("roles") ?? "";
    return new Set(
      raw
        .split(",")
        .map((s) => s.trim())
        .filter((s): s is RoleFilter => ROLE_FILTER_SET.has(s as RoleFilter)),
    );
  }, [searchParams]);

  const setSearch = useCallback((v: string) => {
    setSearchParams(prev => { v ? prev.set("q", v) : prev.delete("q"); return prev; }, { replace: true });
  }, [setSearchParams]);
  const setStatusFilter = useCallback((v: StatusFilter) => {
    setSearchParams(prev => { v !== "active" ? prev.set("status", v) : prev.delete("status"); return prev; }, { replace: true });
  }, [setSearchParams]);
  // VIB-1189: avsluttet-visningen årsavgrenses (default inneværende år) så
  // summeringslinja er sammenlignbar med dashboardet/bonusmålet.
  const currentYear = new Date().getFullYear();
  const completedYear = Number(searchParams.get("year") ?? currentYear);
  // VIB-2103: lagret datamengde per prosjekt. Hentes kun når
  // «Avsluttet»-filteret er aktivt — det er den eneste lista som viser den.
  const { data: storageMap } = useProjectStorage(statusFilter === "completed");
  const setCompletedYear = useCallback((y: number) => {
    setSearchParams(prev => {
      if (y === currentYear) prev.delete("year"); else prev.set("year", String(y));
      // VIB-1288: årsbytte nullstiller måned (unngår f.eks. "februar 2024" utenfor kontekst).
      prev.delete("month");
      return prev;
    }, { replace: true });
  }, [setSearchParams, currentYear]);
  // VIB-1288: månedsfilter i tillegg til år. null = "Alle måneder" (dagens års-oppførsel).
  const monthParam = searchParams.get("month");
  const completedMonth = monthParam ? Number(monthParam) : null;
  const setCompletedMonth = useCallback((m: number | null) => {
    setSearchParams(prev => {
      if (m === null) prev.delete("month"); else prev.set("month", String(m));
      return prev;
    }, { replace: true });
  }, [setSearchParams]);
  const toggleRoleFilter = useCallback((role: RoleFilter) => {
    setSearchParams(prev => {
      const current = new Set(
        (prev.get("roles") ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter((s): s is RoleFilter => ROLE_FILTER_SET.has(s as RoleFilter)),
      );
      if (current.has(role)) current.delete(role); else current.add(role);
      const next = ROLE_FILTERS.filter((r) => current.has(r));
      if (next.length === 0) prev.delete("roles"); else prev.set("roles", next.join(","));
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  // VIB-709-oppfølger (William 29.06): sortering persisteres i URL + storage så
  // den overlever navigering inn/ut av prosjekter. Gjelder Kundeprosjekter-tabellen.
  const sortParam = searchParams.get("sort");
  const sortKey: SortKey =
    sortParam && (SORT_KEYS as string[]).includes(sortParam) ? (sortParam as SortKey) : null;
  const sortDir: SortDir = searchParams.get("dir") === "desc" ? "desc" : "asc";
  const toggleSort = useCallback((key: SortKey) => {
    setSearchParams(prev => {
      const curParam = prev.get("sort");
      const curKey: SortKey =
        curParam && (SORT_KEYS as string[]).includes(curParam) ? (curParam as SortKey) : null;
      const curDir: SortDir = prev.get("dir") === "desc" ? "desc" : "asc";
      const next = nextSortState(curKey, curDir, key);
      if (next.key) prev.set("sort", next.key); else prev.delete("sort");
      if (next.key && next.dir === "desc") prev.set("dir", "desc"); else prev.delete("dir");
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  // Restore filter-state fra localStorage hvis URL ikke har query-params.
  // Kjøres kun én gang ved mount; setSearchParams trigger ny render som
  // henter status/search/roles via URL i neste pass.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (
      searchParams.has("status") || searchParams.has("q") ||
      searchParams.has("roles") || searchParams.has("sort")
    ) {
      return;
    }
    const stored = readStoredFilters();
    if (!stored) return;
    setSearchParams(prev => {
      if (stored.status && stored.status !== "active") prev.set("status", stored.status);
      if (stored.q) prev.set("q", stored.q);
      if (stored.roles) prev.set("roles", stored.roles);
      if (stored.sort && (SORT_KEYS as string[]).includes(stored.sort)) {
        prev.set("sort", stored.sort);
        if (stored.dir === "desc") prev.set("dir", "desc");
      }
      return prev;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Persistér filter ved enhver endring så sidebar-navigering tilbake
  // henter samme state. Hopper over første render hvis vi ikke har restored
  // ennå, så vi ikke skriver default-state over en gyldig storage-verdi.
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      const data: StoredFilters = {
        status: statusFilter,
        q: search || undefined,
        roles: searchParams.get("roles") ?? undefined,
        sort: searchParams.get("sort") ?? undefined,
        dir: searchParams.get("dir") ?? undefined,
        savedAt: Date.now(),
      };
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage utilgjengelig (privat modus, quota) — ignorer stille.
    }
  }, [statusFilter, search, searchParams]);

  const userMap = useMemo(() => buildUserMap(users ?? []), [users]);
  const ownersMap = useMemo(() => buildOwnersMap(owners ?? []), [owners]);

  const statusCounts = useMemo(() => {
    if (!projects) return { active: 0, kommende: 0, completed: 0, all: 0 };
    return {
      active: projects.filter(p => p.project_status === "active").length,
      kommende: projects.filter(p => p.project_status === "kommende").length,
      completed: projects.filter(p => p.project_status === "completed" || p.project_status === "cancelled").length,
      all: projects.length,
    };
  }, [projects]);

  const { external, internal } = useMemo(() => {
    if (!projects) return { external: [], internal: [] };
    const activeUserId = activeUser?.id ?? null;
    const hasRoleFilter = roleFilters.size > 0;
    // Alle innloggede brukere har leserett på alle prosjekter — eventuelle
    // skrive-handlinger gates i prosjektdetaljer (advisor/PL/owner-sjekk).
    const filtered = projects.filter((p) => {
      if (statusFilter === "active" && p.project_status !== "active") return false;
      if (statusFilter === "kommende" && p.project_status !== "kommende") return false;
      if (statusFilter === "completed" && p.project_status !== "completed" && p.project_status !== "cancelled") return false;
      // VIB-1189/1288: årsavgrens (og valgfritt månedsavgrens) avsluttede på completed_at.
      if (statusFilter === "completed") {
        if (!matchesCompletedPeriod(p.completed_at, completedYear, completedMonth)) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!p.name?.toLowerCase().includes(q) && !p.project_code?.toLowerCase().includes(q)) return false;
      }
      if (hasRoleFilter && activeUserId) {
        const isPL = roleFilters.has("pl") && p.production_lead_id === activeUserId;
        const isAdvisor = roleFilters.has("advisor") && p.advisor_id === activeUserId;
        const isSales = roleFilters.has("sales") && (ownersMap.get(p.id) ?? []).includes(activeUserId);
        if (!(isPL || isAdvisor || isSales)) return false;
      }
      return true;
    });
    return {
      external: filtered.filter(p => !p.is_internal),
      internal: filtered.filter(p => p.is_internal),
    };
  }, [projects, search, statusFilter, roleFilters, activeUser, ownersMap, completedYear, completedMonth]);

  // VIB-2528: piltast-lista speiler den VISTE rekkefølgen: samme rene
  // sorteringsfunksjoner som tabellene bruker, kundeprosjekter først.
  const allFiltered = useMemo(() => {
    const hours = hoursMap ?? new Map();
    const ctx = { ownersMap, userMap, hoursMap: hours, invoicedMap: invoicedMap ?? new Map() };
    let sortedExternal: typeof external;
    if (statusFilter === "completed") {
      const byId = new Map(external.map((p) => [p.id, p]));
      sortedExternal = sortCompletedRows(
        external.map((p) => toCompletedRow(p, ownersMap, hours)),
        sortKey,
        sortDir,
        userMap,
      ).map((r) => byId.get(r.id)!);
    } else {
      sortedExternal = sortActiveProjects(external, sortKey, sortDir, ctx);
    }
    return [...sortedExternal, ...sortActiveProjects(internal, sortKey, sortDir, ctx)];
  }, [external, internal, statusFilter, sortKey, sortDir, ownersMap, userMap, hoursMap, invoicedMap]);

  // VIB-1189: tilgjengelige år for avsluttet-velgeren (fra completed_at), nyeste først.
  const completedYears = useMemo(() => {
    const ys = new Set<number>([currentYear]);
    for (const p of projects ?? []) {
      if ((p.project_status === "completed" || p.project_status === "cancelled") && p.completed_at) {
        ys.add(Number(String(p.completed_at).slice(0, 4)));
      }
    }
    return Array.from(ys).sort((a, b) => b - a);
  }, [projects, currentYear]);

  // Tastatur: N, «/», piltaster, Enter, Esc (VIB-2528: følger vist sortering).
  const searchRef = useRef<HTMLInputElement>(null);
  const roleFilterKey = Array.from(roleFilters).sort().join(",");
  const { activeId } = useProjectListKeyboard({
    items: allFiltered,
    navigate,
    searchRef,
    resetKey: `${search}|${statusFilter}|${roleFilterKey}|${completedYear}|${completedMonth}`,
  });

  if (error) {
    return <div className="px-6 py-8"><p className="text-destructive">Feil: {error.message}</p></div>;
  }

  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 min-w-0">
          <SrIcon name="folder-kanban" className="w-6 h-6 text-muted-foreground flex-shrink-0"  />
          <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] text-foreground tracking-wide">
            Alle prosjekter
          </h1>
        </div>
        <Button
          onClick={() => navigate("/prosjekter/ny")}
          size="sm"
          className="min-h-[44px] w-full xl:w-auto"
        >
          <SrIcon name="plus" className="w-4 h-4 mr-1"  />
          Nytt prosjekt
        </Button>
      </div>

      {/* Filters Card */}
      <Card className="mb-6">
        <CardContent className="p-4 sm:p-6">
          {/* Status tabs */}
          <div className="overflow-x-auto overflow-y-hidden mb-4 border-b border-border/50">
            <div className="flex items-center gap-1">
              {(([
                { key: "active" as StatusFilter, label: "Aktive", icon: "circle-dot", count: statusCounts.active },
                { key: "kommende" as StatusFilter, label: "Kommende", icon: "clock", count: statusCounts.kommende },
                { key: "completed" as StatusFilter, label: "Avsluttede", icon: "archive", count: statusCounts.completed },
                { key: "all" as StatusFilter, label: "Alle", icon: "list", count: statusCounts.all },
              ] as Array<{ key: StatusFilter; label: string; icon: SrIconName; count: number }>)).map(({ key, label, icon, count }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap min-h-[44px]",
                    statusFilter === key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  )}
                >
                  <SrIcon name={icon} className="w-4 h-4 shrink-0" />
                  <span>{label}</span>
                  <span className={cn(
                    "ml-1 text-xs px-1.5 py-0.5 rounded-full",
                    statusFilter === key ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  )}>{count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* VIB-1189/1288: år- og månedsvelger for avsluttede */}
          {statusFilter === "completed" && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-medium text-muted-foreground">År:</span>
              <select
                value={completedYear}
                onChange={(e) => setCompletedYear(Number(e.target.value))}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {completedYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <span className="text-xs font-medium text-muted-foreground">Måned:</span>
              <select
                value={completedMonth ?? ""}
                onChange={(e) => setCompletedMonth(e.target.value === "" ? null : Number(e.target.value))}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">Alle måneder</option>
                {MONTH_NAMES_NB.map((name, idx) => (
                  <option key={name} value={idx + 1}>{name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Role filter chips */}
          {activeUser && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-xs font-medium text-muted-foreground mr-1">
                Min rolle:
              </span>
              {(([
                { key: "pl" as RoleFilter, label: "Prosjektleder", icon: "user-cog" },
                { key: "sales" as RoleFilter, label: "Salg", icon: "trending-up" },
                { key: "advisor" as RoleFilter, label: "Kunderådgiver", icon: "user-round" },
              ] as Array<{ key: RoleFilter; label: string; icon: SrIconName }>)).map(({ key, label, icon }) => {
                const active = roleFilters.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleRoleFilter(key)}
                    aria-pressed={active}
                    data-testid={`role-filter-${key}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors min-h-[36px]",
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:text-foreground hover:border-foreground/30",
                    )}
                  >
                    <SrIcon name={icon} className="w-3.5 h-3.5 shrink-0" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <SrIcon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground shrink-0"  />
            <Input
              ref={searchRef}
              placeholder="Søk prosjekt eller kode... (/)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 min-h-[44px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Project Tables */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6 flex items-center justify-center h-32">
            <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground"  />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {statusFilter === "completed" ? (
            // VIB-1189: avsluttede får egne kolonner (lønnsomhet) + summeringslinje.
            <CompletedProjectsTable
              title={`Kundeprosjekter (${external.length})`}
              projects={external}
              ownersMap={ownersMap}
              userMap={userMap}
              hoursMap={hoursMap ?? new Map()}
              storageMap={storageMap}
              navigate={navigate}
              activeProjectId={activeId}
              sortKey={sortKey}
              sortDir={sortDir}
              onToggleSort={toggleSort}
            />
          ) : (
            <ProjectTable
              title={`Kundeprosjekter (${external.length})`}
              projects={external}
              ownersMap={ownersMap}
              userMap={userMap}
              hoursMap={hoursMap ?? new Map()}
              progressMap={progressMap ?? new Map()}
              invoicedMap={invoicedMap ?? new Map()}
              navigate={navigate}
              activeProjectId={activeId}
              sortKey={sortKey}
              sortDir={sortDir}
              onToggleSort={toggleSort}
            />
          )}

          {internal.length > 0 && (
            <ProjectTable
              title={`Interne prosjekter (${internal.length})`}
              projects={internal}
              ownersMap={ownersMap}
              userMap={userMap}
              hoursMap={hoursMap ?? new Map()}
              progressMap={progressMap ?? new Map()}
              navigate={navigate}
              activeProjectId={activeId}
              compact
              sortKey={sortKey}
              sortDir={sortDir}
              onToggleSort={toggleSort}
            />
          )}
        </div>
      )}
    </div>
  );
}
